/**
 * Window-vs-appliance blocking predicates + the opening lists built from
 * them — the ONE place that answers "may this appliance stand in front of
 * this window?".
 *
 * Every pass that positions an appliance must agree on the answer, or a
 * later pass silently undoes an earlier one's decision: the inline
 * enumerator (`computeAppliancePlacements`) and the detached-appliance
 * standalone-spot logic (`generateLayouts`'s fridge-detach pass) both
 * reject a fridge in front of a low window, and the run-end reseat
 * (`endpointSnap.seatFridgeAtOpenRunEnd`) + its scoring twin
 * (`scoring/fridgeRunEnd`) must subtract the same windows from the wall
 * before deciding where the fridge may stand — and must NOT read those
 * windows as where the run ends (`tallApplianceRunSegments`). Pure: geometry
 * in, geometry out.
 */

const {
  INCHES_TO_METERS,
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
} = require('../constants');
const { getAvailableWallSegments } = require('../wallSegments');

/** Appliances that physically extend high enough to block a window
 *  whose bottom edge sits below this threshold. The fridge body itself
 *  is ~70" tall so it blocks anything with a sill < 7'; the stove is
 *  shorter but its range hood reaches the upper-cabinet top (~84"), so
 *  the same threshold applies. */
const HIGH_APPLIANCE_TYPES = new Set(['fridge', 'range']);
const WINDOW_BLOCK_THRESHOLD_FROM_FLOOR = 7 * 12 * INCHES_TO_METERS; // 7'

/**
 * True when this appliance + window pair would visually clash —
 * appliance is a fridge or stove and the window's bottom edge sits
 * below 7' from the floor (so the appliance / hood occludes it).
 */
function applianceBlocksWindow(appliance, window, floorY) {
  if (!HIGH_APPLIANCE_TYPES.has(appliance?.type)) return false;
  const cy = window?.center?.y;
  const wy = window?.dimensions?.y;
  if (typeof cy !== 'number' || typeof wy !== 'number') return false;
  const windowBottomY = cy - wy / 2;
  return windowBottomY < floorY + WINDOW_BLOCK_THRESHOLD_FROM_FLOOR;
}

/**
 * True for a window whose sill sits below the top of an installed
 * lower cabinet (cabinet body + countertop ≈ 36"). Any appliance
 * placement in front of such a window puts a lower-cabinet body and a
 * countertop slab across the bottom of the window opening — visually
 * wrong for sinks (typically under a window but only with a normal
 * sill) and structurally wrong for everything else.
 */
function isLowSillWindow(window, floorY) {
  const cy = window?.center?.y;
  const wy = window?.dimensions?.y;
  if (typeof cy !== 'number' || typeof wy !== 'number') return false;
  const sillY = cy - wy / 2;
  const counterTopY =
    floorY + KITCHEN_CABINET_DIMENSIONS.height + KITCHEN_COUNTERTOP_THICKNESS;
  return sillY < counterTopY;
}

/**
 * The subset of `windows` whose sill sits below the top of a lower cabinet
 * (cabinet body + countertop, ~36"). Any cabinetry in front of one of these
 * puts a body and a countertop slab across the bottom of the opening.
 * Higher-sill windows (a typical kitchen window) aren't included — lowers
 * tuck under those by design.
 */
function lowSillWindows(windows, floorY) {
  if (typeof floorY !== 'number') return [];
  return (windows || []).filter((w) => isLowSillWindow(w, floorY));
}

/**
 * The subset of `windows` a TALL appliance (fridge / range + its hood)
 * would occlude — sill below 7' from the floor.
 */
function highApplianceBlockingWindows(windows, floorY) {
  if (typeof floorY !== 'number') return [];
  const stub = { type: 'fridge' };
  return (windows || []).filter((w) => applianceBlocksWindow(stub, w, floorY));
}

/** An opening is usable as a wall-segment blocker only with real geometry
 *  and a parent wall to project onto. */
function hasProjectableGeometry(opening) {
  return !!(
    opening &&
    opening.center &&
    opening.dimensions &&
    opening.parentWallIds?.length
  );
}

/**
 * The blocker list to hand `getAvailableWallSegments` when the pass is
 * deciding where a TALL appliance may stand: the physical openings plus
 * every window that appliance would occlude. Passing only doors/openings
 * leaves the window span looking like bare wall, so a reseat slides the
 * fridge straight in front of it (#1660) — which is why this list, and not
 * an ad-hoc `[...doors, ...openings]`, is what those passes take.
 *
 * A high transom (sill ≥ 7') is deliberately absent: a fridge under one is
 * fine, and blocking it would sterilize wall the run needs.
 */
function tallApplianceBlockingOpenings(
  { doors = [], openings = [], windows = [] } = {},
  floorY
) {
  return [
    ...doors,
    ...openings,
    ...highApplianceBlockingWindows(windows, floorY),
  ].filter(hasProjectableGeometry);
}

/** Tolerance for "these two segments end at the same place". */
const SAME_END = 1e-6;

/**
 * The TWO stretches of wall a tall appliance is judged against, and the rule
 * for which of its ends is a RUN end.
 *
 *   runSeg — the run: cut by what cabinets stop for (doorways, passages, the
 *            layout's walkways);
 *   seg    — where the tall box may stand: the part of `runSeg` clear of the
 *            windows it would occlude as well.
 *
 * A window limits where a fridge may STAND. It does not end the run: cabinets
 * carry on beneath it, so a fridge drawn up to its edge has cabinetry on both
 * sides and terminates nothing. Read as a run end, a fridge between a window
 * and a doorway had two open ends — ambiguous, so the seat left it mid-run
 * with a stretch of cabinet between it and the doorway, and the score paid
 * the run-end credit to a fridge parked at the glass (2026-09-20). The seat
 * and the score both take their segments from here so they cannot disagree
 * about it again.
 *
 * @param {Function} contains - picks the segment the appliance is in.
 * @returns {{seg, runSeg, endsRun: (end:'lo'|'hi') => boolean} | null}
 */
function tallApplianceRunSegments({
  wall,
  openings = [],
  windows = [],
  floorY,
  corridorRectangles = [],
  contains,
}) {
  const onWall = (o) =>
    hasProjectableGeometry(o) && o.parentWallIds[0] === wall.id;
  const runBlockers = openings.filter(onWall);
  const tallBlockers = [
    ...runBlockers,
    ...highApplianceBlockingWindows(windows, floorY).filter(onWall),
  ];
  const segmentAmong = (blockers) =>
    getAvailableWallSegments(wall, blockers, [], corridorRectangles).find(
      contains
    );
  const seg = segmentAmong(tallBlockers);
  const runSeg = segmentAmong(runBlockers);
  if (!seg || !runSeg) return null;
  const endsRun = (end) =>
    end === 'lo'
      ? Math.abs(seg.start - runSeg.start) < SAME_END
      : Math.abs(seg.end - runSeg.end) < SAME_END;
  return { seg, runSeg, endsRun };
}

module.exports = {
  tallApplianceRunSegments,
  HIGH_APPLIANCE_TYPES,
  WINDOW_BLOCK_THRESHOLD_FROM_FLOOR,
  applianceBlocksWindow,
  isLowSillWindow,
  lowSillWindows,
  highApplianceBlockingWindows,
  tallApplianceBlockingOpenings,
};
